import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Skeleton } from '@ordo/ui/skeleton'
import { Fragment, type ReactNode } from 'react'
import type { Column, FilterOperator, FilterState, SortState } from 'types/data-grid'

/**
 * Phone layout for the grids. Below `md` a table cannot fit a phone (six
 * columns on 343px is under 60px each), so each row becomes a card: a title
 * line, a muted subtitle line and a right-hand badge, chosen per column with
 * `Column.mobile`. Sorting moves to one "Sort by" picker and filtering to one
 * box. The table is unchanged from `md` up.
 */

export interface MobileCardLayout<T> {
  title: Column<T> | undefined
  subtitle: Column<T>[]
  aside: Column<T>[]
}

/** Which column goes where in a phone card. Pure; exported for tests. */
export function mobileCardLayout<T>(columns: Column<T>[]): MobileCardLayout<T> {
  const visible = columns.filter((c) => c.mobile !== 'hidden')
  const title = visible.find((c) => c.mobile === 'title') ?? visible.find((c) => !c.mobile) ?? visible[0]
  return {
    title,
    subtitle: visible.filter((c) => c !== title && (c.mobile === 'subtitle' || !c.mobile)),
    aside: visible.filter((c) => c !== title && c.mobile === 'aside'),
  }
}

function isEmptyCell(node: ReactNode): boolean {
  return node === null || node === undefined || node === false || node === ''
}

export function MobileCardList<T>({
  columns,
  rows,
  keyField,
  cell,
  onRowClick,
  loading,
  emptyMessage,
}: {
  columns: Column<T>[]
  rows: T[]
  keyField: keyof T & string
  cell: (col: Column<T>, row: T) => ReactNode
  onRowClick?: (row: T) => void
  loading: boolean
  emptyMessage: string
}) {
  const layout = mobileCardLayout(columns)

  if (loading) {
    return (
      <ul className="divide-y divide-border/50">
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i} className="flex items-center gap-3 px-4 py-3" data-testid="mobile-card-skeleton">
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            {layout.aside.length > 0 && <Skeleton className="h-5 w-14 rounded-full" />}
          </li>
        ))}
      </ul>
    )
  }

  if (rows.length === 0) {
    return <p className="px-4 py-8 text-center text-muted-foreground">{emptyMessage}</p>
  }

  return (
    <ul className="divide-y divide-border/50">
      {rows.map((row) => {
        const subtitle = layout.subtitle.map((col) => cell(col, row)).filter((node) => !isEmptyCell(node))
        return (
          <li
            key={String((row as Record<string, unknown>)[keyField])}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={
              onRowClick
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onRowClick(row)
                    }
                  }
                : undefined
            }
            role={onRowClick ? 'button' : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            className={
              onRowClick
                ? 'flex min-h-14 cursor-pointer items-center gap-3 px-4 py-3 active:bg-muted/50'
                : 'relative flex min-h-14 items-center gap-3 px-4 py-3 active:bg-muted/50'
            }>
            <div className="min-w-0 flex-1">
              {layout.title && (
                // Without onRowClick the title's own link opens the row; stretch it over the
                // whole card so the tap target is the card, not just the name.
                <div
                  className={
                    onRowClick
                      ? 'truncate font-medium'
                      : "truncate font-medium [&_a]:after:absolute [&_a]:after:inset-0 [&_a]:after:content-['']"
                  }>
                  {cell(layout.title, row)}
                </div>
              )}
              {subtitle.length > 0 && (
                // Cells built for table columns may render blocks; keep them on one flowing line.
                <div className="mt-0.5 line-clamp-2 text-[13px] break-words text-muted-foreground [&_div]:inline [&_p]:inline">
                  {subtitle.map((node, i) => (
                    <Fragment key={i}>
                      {i > 0 && ' · '}
                      {node}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
            {layout.aside.length > 0 && (
              <div className="flex shrink-0 items-center gap-2">
                {layout.aside.map((col) => (
                  <Fragment key={col.key}>{cell(col, row)}</Fragment>
                ))}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

const DEFAULT_SORT = '_default'

/** The phone's one "Sort by" picker over the sortable columns; nothing when none sort. */
export function MobileSortSelect<T>({
  columns,
  sort,
  setSort,
}: {
  columns: Column<T>[]
  sort: SortState | null
  setSort: (value: SortState | null) => void
}) {
  const sortable = columns.filter((c) => c.sortable)
  if (sortable.length === 0) {
    return null
  }
  const value = sort ? `${sort.column}:${sort.direction}` : DEFAULT_SORT
  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (next === DEFAULT_SORT) {
          setSort(null)
          return
        }
        const [column, direction] = next.split(':')
        setSort({ column, direction: direction === 'desc' ? 'desc' : 'asc' })
      }}>
      <SelectTrigger className="h-10 w-auto min-w-0 shrink-0 text-sm" aria-label="Sort by">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={DEFAULT_SORT}>Default order</SelectItem>
        {sortable.flatMap((col) => [
          <SelectItem key={`${col.key}:asc`} value={`${col.key}:asc`}>
            {col.header} ↑
          </SelectItem>,
          <SelectItem key={`${col.key}:desc`} value={`${col.key}:desc`}>
            {col.header} ↓
          </SelectItem>,
        ])}
      </SelectContent>
    </Select>
  )
}

/**
 * The phone's one filter box, for grids without a search box: a "like"
 * filter on the title column when it filters, else the first filterable one.
 */
export function MobileFilterInput<T>({
  columns,
  filters,
  updateFilter,
  clearFilter,
}: {
  columns: Column<T>[]
  filters: Record<string, FilterState>
  updateFilter: (column: string, operator: FilterOperator, value: string) => void
  clearFilter: (column: string) => void
}) {
  const title = mobileCardLayout(columns).title
  const target = title?.filterable ? title : columns.find((c) => c.filterable)
  if (!target) {
    return null
  }
  const current = filters[target.key]
  return (
    <Input
      type="search"
      placeholder={`Filter by ${target.header.toLowerCase()}…`}
      value={current?.operator === 'like' ? current.value : ''}
      onChange={(e) => {
        if (e.target.value) {
          updateFilter(target.key, 'like', e.target.value)
        } else {
          clearFilter(target.key)
        }
      }}
      className="h-10 min-w-0 flex-1 text-sm"
    />
  )
}
